import { INVENTORY_CATEGORY_GROUPS } from "@/shared/constants/estateCategories";
import { getGroupHex } from "@/shared/utils/categoryColors";
import { formatDollars } from "@/shared/utils/formatters";
import type { DonutSegment } from "@/shared/components/DonutChart";

interface CategoryItem {
  category: string;
  balance: number;
}

export interface CategoryTotal {
  category: string;
  value: number;
}

/**
 * Sum |balance| per category, restricted to known categories, sorted desc.
 * Used by the donut chart + legend in both the postloss and lawyer estate
 * inventory views.
 */
export function sumByCategory<T extends CategoryItem>(
  items: T[],
  categoryList: readonly string[],
): CategoryTotal[] {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(item.category, (map.get(item.category) ?? 0) + Math.abs(Number(item.balance)));
  }
  return categoryList
    .filter((cat) => (map.get(cat) ?? 0) > 0)
    .map((cat) => ({ category: cat, value: map.get(cat)! }))
    .sort((a, b) => b.value - a.value);
}

export interface GroupTotal {
  group: string;
  value: number;
  classification: "asset" | "debt";
}

export function sumByGroup(
  items: { category: string; balance: number }[],
  groups: readonly {
    title: string;
    subCategories: readonly string[];
    classification: "asset" | "debt";
  }[],
): GroupTotal[] {
  const catToGroup = new Map<string, { title: string; classification: "asset" | "debt" }>();
  for (const group of groups) {
    for (const cat of group.subCategories) {
      catToGroup.set(cat, { title: group.title, classification: group.classification });
    }
  }

  const sums = new Map<string, number>();
  for (const item of items) {
    const grp = catToGroup.get(item.category);
    if (grp) {
      sums.set(grp.title, (sums.get(grp.title) ?? 0) + Math.abs(Number(item.balance)));
    }
  }

  return groups
    .filter((g) => (sums.get(g.title) ?? 0) > 0)
    .map((g) => ({ group: g.title, value: sums.get(g.title)!, classification: g.classification }));
}

/**
 * Bucket items by their `category` field, preserving order within each bucket.
 */
export function groupItemsByCategory<T extends { category: string }>(
  items: T[],
): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  for (const item of items) {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  }
  return grouped;
}

/**
 * Group debt items by their item_type (e.g. "Mortgages", "Home equity loans").
 */
export function groupDebtsByType<T extends { item_type?: string | null }>(
  debts: T[],
): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  for (const debt of debts) {
    const key = debt.item_type || "Mortgage";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(debt);
  }
  return grouped;
}

export interface EstateDonutData {
  segments: DonutSegment[];
  netValue: number;
  totalAssets: number;
  totalDebts: number;
  assetGroups: GroupTotal[];
  debtGroups: GroupTotal[];
}

interface EstateDonutItem {
  category: string;
  balance: number;
  classification: "asset" | "debt";
  notes?: string | null;
  value_label?: string | null;
}

const MARKED_AS_NONE = "__MARKED_AS_NONE__";

/**
 * Single source of truth for the estate-value donut: filters items, builds
 * per-group asset segments + a single Liabilities segment, and computes the
 * center "Net Estate Value" (valuable items only, raw balance).
 */
export function getEstateDonutData<T extends EstateDonutItem>(items: T[]): EstateDonutData {
  const real = items.filter((i) => i.notes !== MARKED_AS_NONE);

  const groupTotals = sumByGroup(real, INVENTORY_CATEGORY_GROUPS);
  const assetGroups = groupTotals.filter((g) => g.classification === "asset");
  const debtGroups = groupTotals.filter((g) => g.classification === "debt");

  const totalAssets = assetGroups.reduce((sum, g) => sum + g.value, 0);
  const totalDebts = debtGroups.reduce((sum, g) => sum + g.value, 0);

  const valuable = real.filter((i) => !i.value_label);
  const netAssets = valuable
    .filter((i) => i.classification === "asset")
    .reduce((sum, i) => sum + Number(i.balance), 0);
  const netDebts = valuable
    .filter((i) => i.classification === "debt")
    .reduce((sum, i) => sum + Number(i.balance), 0);
  const netValue = netAssets - netDebts;

  const segments: DonutSegment[] = [
    ...assetGroups.map((g) => ({
      key: g.group,
      label: g.group,
      value: g.value,
      formattedValue: formatDollars({ amount: g.value }),
      color: getGroupHex(g.group),
      type: "asset" as const,
    })),
    ...(totalDebts > 0
      ? [
          {
            key: "debts",
            label: "Liabilities",
            value: totalDebts,
            formattedValue: formatDollars({ amount: totalDebts }),
            color: "",
            type: "debt" as const,
          },
        ]
      : []),
  ];

  return { segments, netValue, totalAssets, totalDebts, assetGroups, debtGroups };
}
